import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icons, Popover } from '@/shared/ui';

import styles from './BoardFilterBar.module.css';

export interface FilterOption<T extends string> {
  value: T;
  label: string;
}

interface Props<T extends string> {
  /** The category's name ("Deadline"); also the accessible name of the group. */
  label: string;
  options: readonly FilterOption<T>[];
  selected: readonly T[];
  onChange: (next: T[]) => void;
  /** Toggle one option (the caller decides how a full selection collapses). */
  onToggle: (option: T) => void;
  /** `inline`: the options are shown in place (the phone sheet) instead of in a popover. */
  variant?: 'popover' | 'inline';
  /**
   * Offer the explicit "All" row and "Clear" (the default). A category whose default is a
   * specific option (the planner's "Open") sets this to `false`: there "no restriction" is not
   * an empty selection.
   */
  withAll?: boolean;
}

/**
 * One filter category as a multi-select: checkboxes for the options plus an explicit "All".
 * "All" is checked exactly while nothing specific is selected; choosing it clears the selection
 * and choosing a specific option leaves it - they are never selected together.
 */
export function OptionFilter<T extends string>({
  label,
  options,
  selected,
  onChange,
  onToggle,
  variant = 'popover',
  withAll = true,
}: Props<T>) {
  const { t } = useTranslation();
  const groupId = useId();
  const count = selected.length;
  const group = (
    <fieldset className={styles.optionGroup} aria-labelledby={`${groupId}-legend`}>
      <legend
        id={`${groupId}-legend`}
        className={variant === 'inline' ? undefined : 'visually-hidden'}
      >
        {label}
      </legend>
      {withAll && (
        <label className={styles.option}>
          <input
            type="checkbox"
            checked={count === 0}
            onChange={() => {
              if (count > 0) onChange([]);
            }}
          />
          {t('filter.optionAll')}
        </label>
      )}
      {options.map((option) => (
        <label key={option.value} className={styles.option}>
          <input
            type="checkbox"
            checked={selected.includes(option.value)}
            onChange={() => {
              onToggle(option.value);
            }}
          />
          {option.label}
        </label>
      ))}
    </fieldset>
  );
  if (variant === 'inline') return group;
  return (
    <Popover
      label={label}
      width={260}
      trigger={(props, open) => (
        <button
          type="button"
          {...props}
          className={`${styles.labelsButton} ${count > 0 ? styles.labelsButtonOn : ''}`}
          data-open={open ? 'true' : undefined}
        >
          {count > 0 && withAll ? t('filter.categoryCount', { name: label, count }) : label}
          <Icons.chevronDown />
        </button>
      )}
    >
      <div className={styles.optionPopover}>
        {group}
        {withAll && (
          <div className={styles.optionFooter}>
            <span className={styles.selectedCount}>{t('filter.labelsSelected', { count })}</span>
            <Button
              size="sm"
              variant="ghost"
              disabled={count === 0}
              onClick={() => {
                onChange([]);
              }}
            >
              {t('filter.clear')}
            </Button>
          </div>
        )}
      </div>
    </Popover>
  );
}
